import type { AudioPreferences } from './types';

export const AUDIO_PREFERENCES_KEY = 'slot-yugioh-rework:v2:audio';
export const LEGACY_SETTINGS_KEY = 'ygo_slot_settings_v1';

export const DEFAULT_AUDIO_PREFERENCES: AudioPreferences = Object.freeze({
  version: 2,
  enabled: true,
  masterVolume: 80,
  musicVolume: 72,
  sfxVolume: 86,
});

/** Adapter for the canonical persistence service, whose volume values are normalized to 0..1. */
export interface UnitAudioSettings {
  readonly masterVolume: number;
  readonly musicVolume: number;
  readonly effectsVolume: number;
  readonly masterMuted: boolean;
  readonly musicMuted: boolean;
  readonly effectsMuted: boolean;
}

export function audioPreferencesFromUnitSettings(settings: UnitAudioSettings): AudioPreferences {
  return {
    version: 2,
    enabled: !settings.masterMuted,
    masterVolume: clampPercent(settings.masterVolume * 100, DEFAULT_AUDIO_PREFERENCES.masterVolume),
    musicVolume: settings.musicMuted ? 0 : clampPercent(settings.musicVolume * 100, DEFAULT_AUDIO_PREFERENCES.musicVolume),
    sfxVolume: settings.effectsMuted ? 0 : clampPercent(settings.effectsVolume * 100, DEFAULT_AUDIO_PREFERENCES.sfxVolume),
  };
}

export function unitSettingsPatchFromAudioPreferences(preferences: AudioPreferences): UnitAudioSettings {
  return {
    masterVolume: preferences.masterVolume / 100,
    musicVolume: preferences.musicVolume / 100,
    effectsVolume: preferences.sfxVolume / 100,
    masterMuted: !preferences.enabled,
    musicMuted: preferences.musicVolume === 0,
    effectsMuted: preferences.sfxVolume === 0,
  };
}

function clampPercent(value: unknown, fallback: number): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(100, Math.round(numeric)));
}

export function decodeAudioPreferences(value: unknown): AudioPreferences | null {
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Partial<Record<keyof AudioPreferences, unknown>>;
  if (candidate.version !== 2 || typeof candidate.enabled !== 'boolean') return null;
  return {
    version: 2,
    enabled: candidate.enabled,
    masterVolume: clampPercent(candidate.masterVolume, DEFAULT_AUDIO_PREFERENCES.masterVolume),
    musicVolume: clampPercent(candidate.musicVolume, DEFAULT_AUDIO_PREFERENCES.musicVolume),
    sfxVolume: clampPercent(candidate.sfxVolume, DEFAULT_AUDIO_PREFERENCES.sfxVolume),
  };
}

export function migrateLegacyAudioPreferences(value: unknown): AudioPreferences | null {
  if (typeof value !== 'object' || value === null) return null;
  const legacy = value as Record<string, unknown>;
  if (typeof legacy.soundEnabled !== 'boolean') return null;
  const draw = clampPercent(legacy.drawSoundVolume, DEFAULT_AUDIO_PREFERENCES.sfxVolume);
  const shuffle = clampPercent(legacy.shuffleSoundVolume, draw);
  const musicMuted = legacy.baseMusicMuted === true;
  const drawMuted = legacy.drawSoundMuted === true;
  const shuffleMuted = legacy.shuffleSoundMuted === true;
  return {
    version: 2,
    enabled: legacy.soundEnabled,
    masterVolume: clampPercent(legacy.masterVolume, DEFAULT_AUDIO_PREFERENCES.masterVolume),
    musicVolume: musicMuted ? 0 : clampPercent(legacy.baseMusicVolume, DEFAULT_AUDIO_PREFERENCES.musicVolume),
    sfxVolume: drawMuted && shuffleMuted ? 0 : Math.round((draw + shuffle) / 2),
  };
}

export function loadAudioPreferences(storage: Storage): AudioPreferences {
  try {
    const currentRaw = storage.getItem(AUDIO_PREFERENCES_KEY);
    if (currentRaw !== null) {
      const decoded = decodeAudioPreferences(JSON.parse(currentRaw) as unknown);
      if (decoded !== null) return decoded;
    }
    const legacyRaw = storage.getItem(LEGACY_SETTINGS_KEY);
    if (legacyRaw !== null) {
      const migrated = migrateLegacyAudioPreferences(JSON.parse(legacyRaw) as unknown);
      if (migrated !== null) {
        saveAudioPreferences(storage, migrated);
        return migrated;
      }
    }
  } catch {
    // Optional preferences must never prevent boot.
  }
  return DEFAULT_AUDIO_PREFERENCES;
}

export function saveAudioPreferences(storage: Storage, preferences: AudioPreferences): void {
  try {
    storage.setItem(AUDIO_PREFERENCES_KEY, JSON.stringify(preferences));
  } catch {
    // Storage can be unavailable in private browsing or embedded contexts.
  }
}
